import { GrowthBook, WidenPrimitives } from '@growthbook/growthbook';

import {
    TargetingAttributes,
    UpdateTargetingAttributesParam,
    transformToGrowthBookAttributes,
} from '@vtblife/growthbook';

import { reportErrorToSentry } from '../shell/utils/sentry-reporter';

// TODO: DLK-382 Рассмотреть возможность выноса layout-components/feature-manager в отдельный пакет.
// Т.к. библиотека vtblife/layout-components тяжелая с большим количеством зависимостей.
export class FeatureManager {
    private readonly client: GrowthBook;
    private readonly initialTargetingAttributes: TargetingAttributes;

    constructor(initialState: InitialState) {
        const environment = initialState.configStore?.appEnvironment;

        this.initialTargetingAttributes = initialState.growthBookData?.targetingAttributes ?? {};

        this.client = new GrowthBook({
            enableDevMode: environment !== 'production',
        });

        const payload = initialState.growthBookData?.payload;
        if (payload) {
            this.client.initSync({
                streaming: false,
                payload,
            });
        } else {
            const error = 'FeatureManager: Payload is empty. Unable to initialize.';
            console.error(error);
            reportErrorToSentry({ error });
        }

        this.initAttributes();
    }

    /** Получение значения фичи.
     * При попадании в эксперимент отправляется событие просмотра эксперимента в аналитику (всегда).
     * @see хук useFeatureValue рекомендуется для простых сценариев использования.
     */
    getFeatureValue<T>(featureName: string, defaultValue: T): WidenPrimitives<T> {
        const featureResult = this.client.evalFeature<WidenPrimitives<T>>(featureName);

        if (featureResult.source === 'experiment' && featureResult.experiment && featureResult.experimentResult) {
            window.dataLayer.push({
                event: 'experiment_viewed',
                experiment_id: featureResult.experiment.key,
                variation_id: featureResult.experimentResult.key,
                hash_attribute: featureResult.experimentResult.hashAttribute,
                hash_value: featureResult.experimentResult.hashValue,
            });
        }

        if (this.client.inDevMode()) {
            console.debug('featureManager.getFeatureValue', {
                client: this.client,
                featureName,
                defaultValue,
                featureResult,
            });
        }

        return featureResult.value ?? (defaultValue as WidenPrimitives<T>);
    }

    getExperimentHash(featureName: string): string | undefined {
        const featureResult = this.client.evalFeature(featureName);

        if (!featureResult.experiment || !featureResult.experimentResult) {
            return undefined;
        }

        return (
            featureResult.experimentResult.hashAttribute +
            featureResult.experimentResult.hashValue +
            featureResult.experiment.key +
            featureResult.experimentResult.variationId
        );
    }

    async updateAttributes(updateAttributes: UpdateTargetingAttributesParam) {
        const growthBookAttributes = transformToGrowthBookAttributes(updateAttributes);
        await this.client.updateAttributes(growthBookAttributes);

        if (this.client.inDevMode()) {
            console.debug('featureManager.updateAttributes', {
                client: this.client,
                initialTargetingAttributes: this.initialTargetingAttributes,
                transformed: growthBookAttributes,
            });
        }
    }

    private initAttributes() {
        const growthBookAttributes = transformToGrowthBookAttributes(this.initialTargetingAttributes);

        if (this.client.inDevMode()) {
            console.debug('featureManager.initAttributes', {
                client: this.client,
                initialTargetingAttributes: this.initialTargetingAttributes,
                transformed: growthBookAttributes,
            });
        }

        if (!growthBookAttributes['anonymousUserId']) {
            const error = 'FeatureManager: anonymousUserId targeting attribute is empty.';
            console.warn(error);
            reportErrorToSentry({ error });
        }

        this.client.updateAttributes(growthBookAttributes);
    }
}
